/**
 * The ranked hotspot list: a real table at ≥ md (caption, sortable
 * headers with `aria-sort`), one card per hotspot below it.
 */
import type { HotspotBy, HotspotRow } from "@iamu/shared";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  DirectionChip,
  FindingDeltaBadges,
  formatPressure,
  KEY_LABEL,
  OutcomeChip,
  PressureBar,
  problemSummary,
  Sparkline,
  statusNote,
} from "./hotspot-vocabulary";

import type { HotspotSortCol, HotspotSortDir } from "./hotspot-url-state";

const passRate = (r: HotspotRow) => {
  const o = r.outcome;
  return o ? o.pass / (o.pass + o.partial + o.fail) : 2;
};

const VALUE: Record<
  Exclude<HotspotSortCol, "rank">,
  (r: HotspotRow) => number | string | null
> = {
  area: (r) => r.label.toLowerCase(),
  pressure: (r) => r.pressure,
  open: (r) => r.openProblems + r.awaitingRerun,
  outcome: passRate,
  runs: (r) => r.runs,
  steps: (r) => r.exposureSteps,
};

function sortRows(
  rows: HotspotRow[],
  col: HotspotSortCol,
  dir: HotspotSortDir,
): HotspotRow[] {
  // Rank is the server's order (`compareHotspotRows`), not a column.
  if (col === "rank") return rows;
  const v = VALUE[col];
  const sign = dir === "ascending" ? 1 : -1;
  // Stable, so ties keep the rank order. Rows with no value (not walked)
  // sort last in either direction.
  return [...rows].sort((a, b) => {
    const x = v(a);
    const y = v(b);
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    return (x < y ? -1 : x > y ? 1 : 0) * sign;
  });
}

interface ListProps {
  by: HotspotBy;
  rows: HotspotRow[];
  sortCol: HotspotSortCol;
  sortDir: HotspotSortDir;
  onSort: (col: HotspotSortCol) => void;
  /** Link that opens a hotspot's panel. */
  hrefFor: (key: string) => string;
  /** The row the formula popover explains (focus / hover). */
  onFocusRow: (key: string) => void;
}

export function HotspotTable({
  by,
  rows,
  sortCol,
  sortDir,
  onSort,
  hrefFor,
  onFocusRow,
}: ListProps) {
  const sorted = useMemo(
    () => sortRows(rows, sortCol, sortDir),
    [rows, sortCol, sortDir],
  );
  const max = Math.max(0, ...rows.map((r) => r.pressure ?? 0));
  const ranked = sortCol === "rank";
  const sortLabel =
    sortCol === "area" ? KEY_LABEL[by] : SORT_LABEL[sortCol as SortCol];

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        {ranked ? (
          RANK_RULE
        ) : (
          <>
            Sorted by {sortLabel.toLowerCase()},{" "}
            {sortCol === "area"
              ? sortDir === "descending"
                ? "Z to A"
                : "A to Z"
              : sortDir === "descending"
                ? "highest first"
                : "lowest first"}
            .{" "}
            <button
              type="button"
              onClick={() => onSort("rank")}
              className="inline-flex min-h-6 items-center rounded-sm text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Back to weakest first
            </button>
          </>
        )}
      </p>
      <HotspotTableBody
        by={by}
        rows={sorted}
        max={max}
        sortCol={sortCol}
        sortDir={sortDir}
        onSort={onSort}
        hrefFor={hrefFor}
        onFocusRow={onFocusRow}
        caption={
          ranked
            ? `Hotspots by ${KEY_LABEL[by].toLowerCase()}. ${RANK_RULE}`
            : `Hotspots by ${KEY_LABEL[by].toLowerCase()}, sorted by ${sortLabel.toLowerCase()}.`
        }
      />
    </div>
  );
}

type SortCol = Exclude<HotspotSortCol, "rank" | "area">;

const SORT_LABEL: Record<SortCol, string> = {
  pressure: "Pressure",
  open: "Problems",
  outcome: "Human tests",
  runs: "Runs",
  steps: "Steps",
};

/** The default order, in words (the table's caption and lead line). */
const RANK_RULE =
  "Weakest first: failing human tests, then regressions, then pressure.";

function HotspotTableBody({
  by,
  rows: sorted,
  max,
  sortCol,
  sortDir,
  onSort,
  hrefFor,
  onFocusRow,
  caption,
}: Omit<ListProps, "rows"> & {
  rows: HotspotRow[];
  max: number;
  caption: string;
}) {
  return (
    <Table aria-label={`Hotspots by ${KEY_LABEL[by].toLowerCase()}`}>
      <TableCaption className="sr-only">{caption}</TableCaption>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <SortableTableHead
            col="area"
            label={KEY_LABEL[by]}
            activeCol={sortCol}
            dir={sortDir}
            onSort={onSort}
            className="w-full min-w-48"
          />
          <SortableTableHead
            col="pressure"
            label={SORT_LABEL.pressure}
            activeCol={sortCol}
            dir={sortDir}
            onSort={onSort}
            className="min-w-44"
          />
          <SortableTableHead
            col="open"
            label={SORT_LABEL.open}
            activeCol={sortCol}
            dir={sortDir}
            onSort={onSort}
          />
          <SortableTableHead
            col="outcome"
            label={SORT_LABEL.outcome}
            activeCol={sortCol}
            dir={sortDir}
            onSort={onSort}
          />
          <TableHead className="px-3">Trend</TableHead>
          <SortableTableHead
            col="runs"
            label={SORT_LABEL.runs}
            activeCol={sortCol}
            dir={sortDir}
            onSort={onSort}
            align="right"
          />
          <SortableTableHead
            col="steps"
            label={SORT_LABEL.steps}
            activeCol={sortCol}
            dir={sortDir}
            onSort={onSort}
            align="right"
          />
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((row) => {
          const note = statusNote(row);
          return (
            <TableRow
              key={row.key}
              onMouseEnter={() => onFocusRow(row.key)}
              onFocus={() => onFocusRow(row.key)}
              className="align-top"
            >
              <TableCell className="whitespace-normal px-3 py-3">
                <Link
                  to={hrefFor(row.key)}
                  title={row.label}
                  className="line-clamp-2 text-base font-semibold text-foreground underline-offset-4 hover:underline"
                >
                  {row.label}
                </Link>
                {row.context && (
                  <p className="text-sm text-muted-foreground">{row.context}</p>
                )}
                {note && (
                  <p className="text-sm text-muted-foreground">{note}</p>
                )}
              </TableCell>
              <TableCell className="px-3 py-3">
                <div className="flex items-center gap-2">
                  <span className="w-10 text-right text-base font-semibold tabular-nums">
                    {formatPressure(row.pressure)}
                  </span>
                  <PressureBar row={row} max={max} className="w-28" />
                </div>
              </TableCell>
              <TableCell className="px-3 py-3 text-sm tabular-nums">
                {row.status === "not_walked" ? (
                  "—"
                ) : (
                  <div className="space-y-1">
                    <div>{problemSummary(row)}</div>
                    <FindingDeltaBadges deltas={row.deltas} />
                  </div>
                )}
              </TableCell>
              <TableCell className="px-3 py-3">
                <OutcomeChip outcome={row.outcome} />
              </TableCell>
              <TableCell className="px-3 py-3">
                <div className="flex items-center gap-2">
                  <Sparkline points={row.trend} />
                  <DirectionChip direction={row.direction} />
                </div>
              </TableCell>
              <TableCell className="px-3 py-3 text-right tabular-nums">
                {row.runs}
              </TableCell>
              <TableCell className="px-3 py-3 text-right tabular-nums">
                {row.exposureSteps}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

interface HotspotCardsProps {
  by: HotspotBy;
  rows: HotspotRow[];
  hrefFor: (key: string) => string;
}

/** Below md: one card per hotspot, full-width bar, no horizontal scroll. */
export function HotspotCards({ by, rows, hrefFor }: HotspotCardsProps) {
  const max = Math.max(0, ...rows.map((r) => r.pressure ?? 0));
  return (
    <ol
      aria-label={`Hotspots by ${KEY_LABEL[by].toLowerCase()}, weakest first`}
      className="divide-y divide-border/60"
    >
      {rows.map((row) => {
        const note = statusNote(row);
        return (
          <li key={row.key} className="space-y-2 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <Link
                to={hrefFor(row.key)}
                className="min-w-0 text-base font-semibold text-foreground underline-offset-4 hover:underline"
              >
                {row.label}
              </Link>
              <span className="shrink-0 text-base font-semibold tabular-nums">
                <span className="sr-only">Pressure </span>
                {formatPressure(row.pressure)}
              </span>
            </div>
            {row.context && (
              <p className="text-sm text-muted-foreground">{row.context}</p>
            )}
            <PressureBar row={row} max={max} className="w-full" />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm tabular-nums">
              {row.status !== "not_walked" && (
                <span>{problemSummary(row)}</span>
              )}
              <FindingDeltaBadges deltas={row.deltas} />
              <OutcomeChip outcome={row.outcome} />
              <DirectionChip direction={row.direction} />
            </div>
            {note && <p className="text-sm text-muted-foreground">{note}</p>}
          </li>
        );
      })}
    </ol>
  );
}
